import type { ReactNode } from 'react';
import * as Tooltip from '~/components/ui/Tooltip';
import type { PricingCardMessage, PricingPerk } from '~/lib/stores/pricing/types';
import type { SubscriptionInterval } from '~/types/subscription';
import { classNames } from '~/utils/classNames';
import type { Pricing } from '~/utils/pricing';

interface PricingCardMessagesProps {
  messages: PricingCardMessage[];
}

export const PricingCardMessages = ({ messages }: PricingCardMessagesProps) => {
  return (
    <div className="flex flex-col gap-1 text-sm text-bolt-ds-textTertiary">
      {messages.map((message) => {
        const color = message.color === 'warning' ? 'text-bolt-ds-warning' : '';
        const align = message.align === 'center' ? 'text-center' : 'text-left';

        if (message.tooltipText) {
          return (
            <Tooltip.Provider key={message.text}>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <span className={classNames(color, align)}>{message.text}</span>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    side="bottom"
                    avoidCollisions
                    collisionPadding={10}
                    sideOffset={6}
                    maxWidth={250}
                    truncate={false}
                  >
                    <span className="text-center">{message.tooltipText}</span>
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          );
        }

        return (
          <span key={message.text} className={classNames(color, align)}>
            {message.text}
          </span>
        );
      })}
    </div>
  );
};

interface PricingPerksProps {
  label: string;
  items: PricingPerk[];
  className?: string;
}

export const PricingPerks = ({ label, items, className }: PricingPerksProps) => {
  return (
    <Tooltip.Provider>
      <div className={classNames('flex flex-col gap-2', className)}>
        <b className="text-xs font-semibold text-bolt-ds-textPrimary">{label}</b>

        <ul className="flex flex-col gap-3">
          {items.map((item) => {
            const itemText = typeof item === 'string' ? item : item.text;
            const itemTooltip = typeof item === 'string' ? undefined : item.tooltip;

            const itemIcon =
              typeof item === 'string' || !item.icon || item.icon === 'check'
                ? 'i-heroicons:check size-4 text-bolt-ds-success '
                : 'size-1.5 bg-bolt-ds-iconSecondary rounded-full';

            if (itemTooltip) {
              return (
                <li key={itemText} className="flex items-center gap-2 text-xs text-bolt-ds-textSecondary">
                  <div className="flex items-center justify-center size-5">
                    <span className={classNames(itemIcon)} />
                  </div>
                  <span className="text-balance">
                    {itemText}
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <span className="inline-flex items-center justify-center shrink-0 ml-1 translate-y-0.5 i-heroicons:question-mark-circle text-sm text-bolt-ds-iconSecondary" />
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content side="bottom" sideOffset={6} truncate={false} maxWidth={200}>
                          {itemTooltip}
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  </span>
                </li>
              );
            }

            return (
              <li key={itemText} className="flex items-center gap-2 text-xs text-bolt-ds-textSecondary">
                <div className="flex items-center justify-center size-5">
                  <span className={classNames(itemIcon)} />
                </div>
                <span>{itemText}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </Tooltip.Provider>
  );
};

interface PricingSettingSectionWrapperProps {
  children: ReactNode;
  className?: string;
}

export const PricingSettingSectionWrapper = ({ children, className }: PricingSettingSectionWrapperProps) => {
  return (
    <div className="relative flex items-center justify-center p-px bg-bolt-ds-borderOutline rounded-lg z-0">
      <div className="absolute top-0 left-0 size-4 rounded-full bg-bolt-ds-brand z-0" />
      <div className="absolute top-0 left-2 h-px w-1/2 bg-gradient-to-r from-bolt-ds-brand to-transparent" />
      <div className="absolute left-0 top-2 w-px h-1/2 bg-gradient-to-b from-bolt-ds-brand to-transparent" />

      <div className={classNames('w-full bg-bolt-ds-surfaceOne rounded-[7px] p-6 z-1', className)}>{children}</div>
    </div>
  );
};

interface PricingPriceProps {
  pricing: Pricing;
  interval: SubscriptionInterval;
  label: string;
  size?: 'md' | 'sm';
  price?: number;
}

export const PricingPrice = ({ pricing, interval, label, size = 'md', price }: PricingPriceProps) => {
  const planPrice = interval === 'yearly' ? pricing.rateMonthly.year : pricing.rateMonthly.month;

  return (
    <div className="flex items-center gap-2">
      <span
        className={classNames(
          'text-bolt-ds-textPrimary',
          size == 'md' ? 'text-9.5 leading-11.5 font-medium' : 'text-3xl font-semibold',
        )}
      >
        ${typeof price === 'number' ? price : planPrice}
      </span>

      <div className={classNames('flex flex-col', size === 'md' ? 'text-sm' : 'text-xs text-bolt-ds-textSecondary')}>
        <span>{label}</span>

        <span>billed {interval}</span>
      </div>
    </div>
  );
};
